import PropTypes from 'prop-types';
import { Button, Card, Heading, Inline, RadioGroup, Stack } from '@etg/wings';
import { ContentWrapper, ExpandableContent, variants } from '@eti/components';
import { css, mediaQueries } from '@eti/styles';
import { BrandedFare as BrandedFareType } from '@eti/types';
import { CaretDown } from '@phosphor-icons/react';
import { useProperty, useTranslation } from '@eti/providers';
import { useEffect } from 'react';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import { buttonStyles } from '../YourSelectedTripPage';
import BrandedFare from './BrandedFare';

const listingWrapperStyles = (isMediumScreenWidth, numberOfItems) => css`
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr;
  width: 100%;

  @media ${mediaQueries.medium.up} {
    grid-template-columns: ${numberOfItems < 3 || isMediumScreenWidth ? '1fr 1fr' : '1fr 1fr 1fr'};
  }
`;

const toggleButtonWrapperStyles = css`
  display: flex;
  margin-top: 8px;
  position: relative;
`;

const toggleButtonStyles = css`
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
`;

const iconStyles = (isShowingAllOptions) => css`
  transform: rotate(${isShowingAllOptions ? '180deg' : '0deg'});
`;

const cardStyles = css`
  margin-top: 16px;
`;

const bottomContentWrapperStyles = css`
  display: flex;
  flex-direction: column;
  margin: 24px 0;

  > * {
    width: 100%;
  }
`;

const proceedButtonWrapperStyles = css`
  display: flex;
  justify-content: center;
`;

const useBrandedFareOptions = (
  brandedFares,
  selectedBrandedFareSelectionKey,
  isMediumScreenWidth,
) => {
  useEffect(() => {
    sessionStorage.setItem('brandedFareOffered', 'true');
  }, []);

  const { p } = useProperty();
  const numberOfBrandedFaresToShow = isMediumScreenWidth ? 2 : 3;
  const isContentToggleable = brandedFares.length > numberOfBrandedFaresToShow;
  const alwaysVisibleOptions = brandedFares.slice(0, numberOfBrandedFaresToShow);
  const toggleableOptions = isContentToggleable
    ? brandedFares.slice(numberOfBrandedFaresToShow)
    : null;

  const isSelectedBrandedFareAmongToggleableOptions = toggleableOptions?.some(
    (toggleableOption) => toggleableOption.selectionKey === selectedBrandedFareSelectionKey,
  );

  const { isOpen, handleToggle } = useToggle(
    isContentToggleable &&
      (p('BrandedFares.ShowMore.DefaultOpen') || isSelectedBrandedFareAmongToggleableOptions),
  );

  return { alwaysVisibleOptions, handleToggle, isContentToggleable, isOpen, toggleableOptions };
};

const shouldShowInformationBox = (carriersToHideInformationBox, ticketingCarrierCodes) => {
  const hideInformationBoxForCarrier = ticketingCarrierCodes?.some((ticketingCarrierCode) =>
    carriersToHideInformationBox?.includes(ticketingCarrierCode),
  );
  return !hideInformationBoxForCarrier;
};

// TODO WEB-5485 write flowtests when this component uses real (mocked) data.

const BrandedFares = ({
  brandedFares = [],
  handleProceedButtonClick,
  selectedExtraProducts,
  selectedBrandedFareSelectionKey,
  setSelectedBrandedFareSelectionKey,
  ticketingCarrierCodes,
  isMediumScreenWidth,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { alwaysVisibleOptions, handleToggle, isContentToggleable, isOpen, toggleableOptions } =
    useBrandedFareOptions(brandedFares, selectedBrandedFareSelectionKey, isMediumScreenWidth);

  const shouldShowInformationBoxInFirstOption = shouldShowInformationBox(
    p('BrandedFares.HideInformationBox.Carrier'),
    ticketingCarrierCodes,
  );

  const handleSelectBrandedFare = (event) => {
    setSelectedBrandedFareSelectionKey(event.target.value);
  };

  return (
    <ContentWrapper>
      <Card className={cardStyles} data-testid="brandedFares">
        <RadioGroup
          name="brandedFares"
          onChange={handleSelectBrandedFare}
          selectedValue={selectedBrandedFareSelectionKey}
        >
          <Stack spacing={8}>
            <Heading as="legend" level={2}>
              {t('BrandedFares.Header')}
            </Heading>
            <p>{t('BrandedFares.Text')}</p>
            {isContentToggleable && (
              <Inline align="end">
                <p>
                  {t(
                    'BrandedFares.NumberOfTrips.Text',
                    isOpen ? brandedFares.length : alwaysVisibleOptions.length,
                    brandedFares.length,
                  )}
                </p>
              </Inline>
            )}
            <Stack spacing={16}>
              <div
                className={listingWrapperStyles(isMediumScreenWidth, alwaysVisibleOptions.length)}
              >
                {alwaysVisibleOptions.map((brandedFareData, index) => (
                  <BrandedFare
                    brandedFareData={brandedFareData}
                    key={brandedFareData.id}
                    shouldShowInformationBox={index === 0 && shouldShowInformationBoxInFirstOption}
                    ticketingCarrierCodes={ticketingCarrierCodes}
                  />
                ))}
              </div>
              {toggleableOptions && (
                <ExpandableContent isExpanded={isOpen}>
                  <div className={listingWrapperStyles(isMediumScreenWidth)}>
                    {toggleableOptions.map((brandedFareData) => (
                      <BrandedFare
                        brandedFareData={brandedFareData}
                        key={brandedFareData.id}
                        ticketingCarrierCodes={ticketingCarrierCodes}
                      />
                    ))}
                  </div>
                </ExpandableContent>
              )}
            </Stack>
            {isContentToggleable && (
              <div className={toggleButtonWrapperStyles}>
                <Button
                  className={toggleButtonStyles}
                  data-testid="brandedFares-toggleOptions-button"
                  iconSuffix={<CaretDown className={iconStyles(isOpen)} />}
                  onClick={handleToggle}
                  size="small"
                >
                  {isOpen
                    ? t('BrandedFares.NumberOfTrips.ShowLess.Label')
                    : t('BrandedFares.NumberOfTrips.ShowMore.Label', toggleableOptions.length)}
                </Button>
              </div>
            )}
          </Stack>
        </RadioGroup>
      </Card>
      <section
        className={bottomContentWrapperStyles}
        data-testid="brandedfares-v1-continue-button-wrapper"
      >
        <ContentWrapper className={proceedButtonWrapperStyles}>
          <Button
            className={buttonStyles}
            data-testid="brandedfares-v1-proceedButton"
            onClick={() => handleProceedButtonClick(selectedExtraProducts)}
            variant={variants.PRIMARY}
          >
            {t('YourSelectedTrip.Page.ProceedButton.Label')}
          </Button>
        </ContentWrapper>
      </section>
    </ContentWrapper>
  );
};

BrandedFares.propTypes = {
  brandedFares: PropTypes.arrayOf(BrandedFareType),
  handleProceedButtonClick: PropTypes.func,
  isMediumScreenWidth: PropTypes.bool,
  selectedBrandedFareSelectionKey: PropTypes.string,
  selectedExtraProducts: PropTypes.arrayOf(PropTypes.number),
  setSelectedBrandedFareSelectionKey: PropTypes.func,
  ticketingCarrierCodes: PropTypes.arrayOf(PropTypes.string),
};

export default BrandedFares;
